<template>
  <VisibilityTransferFullScreenMap
    v-slot="{ fullScreen }"
    v-model:visibility-map-state="visibilityMapState"
    v-model:visibility-map-ref="visibilityMapRef"
    :points="transfers"
  >
    <Box :aspect="0.4" class="map-container mb-15" :class="{ '!h-screen !w-screen': fullScreen }">
      <ShowTransfersMap
        v-model:visibility-map-ref="visibilityMapRef"
        v-model:visibility-map-state="visibilityMapState"
        :points="transfers"
        :show-info="true"
        :hide-inner-circle="true"
      >
        <template #info="{ point, dataType }">
          <div class="p-5">
            <h6>{{ `${point.locOldName ?? $t('unknown')} -> ${ point.locNewName }` }}</h6>
            <h6>{{ `${ filteredTransfersByDataType(point, dataType)?.length } Transfer(s)` }}</h6>
            <div v-for="transfer in filteredTransfersByDataType(point, dataType)" :key="transfer.id" class="p-2">
              <p>
                {{ transfer.locOldName ?? $t('unknown') }} -> {{ transfer.locNewName }}
                {{ ' | ' + $t('pages.visibility.bicycle.number') + ' ' + transfer.transfer.visibilityBicycle?.number }}
                <button
                  class="mt-1 ml-2 px-1 py-2 text-sm rounded border"
                  @click="() => openConfirmationModal(transfer.id)"
                >
                  {{ $t('pages.visibility.transfer.actions.confirm') }}
                </button>
              </p>
            </div>
            <button
              class="p-2 text-sm rounded border"
              @click="(e) => {
                if (e) e.stopPropagation();

                addLocation({
                  id: point.id + dataType,
                  name: dataType === 'new' ? point.locNewName : point.locOldName,
                  lat: (dataType === 'new' ? point.gisLocNew?.lat : point.gisLocOld?.lat) ?? 0,
                  lng: (dataType === 'new' ? point.gisLocNew?.long : point.gisLocOld?.long) ?? 0,
                });
              }"
            >
              {{ "+ Route" }}
            </button>
          </div>
        </template>
      </ShowTransfersMap>
    </Box>
  </VisibilityTransferFullScreenMap>
</template>

<script lang="ts" setup>
import Box from '@utils/Box.vue';
import { computed, reactive, ref } from 'vue';
import ShowTransfersMap from '@utils/maps/ShowTransfersMap.vue';
import VisibilityTransferFullScreenMap
  from '@pages/visibility/visibilityTransfer/VisibilityTransferFullScreenMap.vue';
import type {
  StackMapLocation,
  VisibilityMapState,
  VisibilityTransfer,
  VisibilityTransferMapData,
} from '@/entities/Entities';
import { defaultCenter } from '@/utils/map';

interface Props {
  transfers?: VisibilityTransfer[];
  openConfirmationModal: (visibilityPlanningId: string) => void;
}

const props = defineProps<Props>();
const transfers = computed(() => props.transfers?.filter((t) => t).map((transfer: VisibilityTransfer) => ({
  id: transfer.id,
  done: transfer.done,
  transfer,
  locNewName: transfer.newLocation?.name,
  locOldName: transfer.oldLocation?.name,
  gisLocNew: transfer.newLocation?.gisLocation,
  gisLocOld: transfer.oldLocation?.gisLocation,
  radius: 30,
}))) ?? [];

const filteredTransfersByDataType = computed(() => (point: VisibilityTransferMapData, dataType: 'new' | 'old') => {
  if (dataType === 'new') {
    return transfers.value?.filter(
      (t: any) =>
        t.gisLocNew?.lat === point.gisLocNew?.lat &&
        t.gisLocNew?.long === point.gisLocNew?.long,
    );
  }
  return transfers.value?.filter(
    (t: any) =>
      t.gisLocOld?.lat === point.gisLocOld?.lat &&
        t.gisLocOld?.long === point.gisLocOld?.long,
  );
});

const visibilityMapRef = ref();
const visibilityMapState = ref<VisibilityMapState>({
  locationStack: [] as StackMapLocation[],
  currentPosition: null,
  showCurrentPosition: false,
  center: defaultCenter,
});

const addLocation = (location: StackMapLocation) => {
  if (!visibilityMapState.value.locationStack.some((loc) => loc.id === location.id)) {
    visibilityMapState.value.locationStack.push(location);
  }
};
</script>
